import type { FormInstance } from "antd";
import { Form, Select } from "antd";
import { useBrigadeList } from "@/modules/brigades/hooks/useBrigade";
import { TASK_TARGET_TYPE_OPTIONS } from "../constants";
import { PlantingMultiSelect } from "./PlantingMultiSelect";
import type { TaskLocationDraft } from "./TaskLocationListInput";
import { TaskLocationListInput } from "./TaskLocationListInput";

interface TaskTargetFieldsProps {
	form: FormInstance;
	organizationId?: string;
}

/**
 * Brigade + target-type + (locations or plantings) — shared between TaskForm
 * and TaskScheduleForm, since both need the exact same fields at creation.
 * Only used at creation: brigade/targetType never change after creation, and
 * the location/planting set is edited afterward via TaskCompletionList's
 * add/remove actions, not through this form.
 */
export function TaskTargetFields({ form, organizationId }: TaskTargetFieldsProps) {
	const targetType = Form.useWatch("targetType", form);

	const { data: brigadesData, isLoading: brigadesLoading } = useBrigadeList({
		organizationId: organizationId ?? "",
		page: 1,
		limit: 100,
	});
	const brigades = brigadesData?.data?.items ?? [];

	return (
		<>
			<Form.Item
				name="brigadeId"
				label="Brigada"
				rules={[{ required: true, message: "Brigadani tanlang" }]}
			>
				<Select
					placeholder={organizationId ? "Brigadani tanlang" : "Avval tashkilotni tanlang"}
					disabled={!organizationId}
					loading={brigadesLoading}
					showSearch
					optionFilterProp="label"
					options={brigades.map((b) => ({ value: b.id, label: b.name }))}
				/>
			</Form.Item>
			<Form.Item
				name="targetType"
				label="Topshiriq turi"
				rules={[{ required: true, message: "Topshiriq turini tanlang" }]}
			>
				<Select options={TASK_TARGET_TYPE_OPTIONS} placeholder="Tanlang" />
			</Form.Item>
			{targetType === "LOCATION" && (
				<Form.Item
					name="locations"
					label="Joylashuvlar"
					rules={[
						{
							validator: async (_, value: TaskLocationDraft[] | undefined) => {
								if (!value || value.length === 0) {
									throw new Error("Kamida bitta joylashuv qo'shing");
								}
								if (value.some((entry) => !entry.geometry)) {
									throw new Error("Har bir joylashuv uchun xaritada belgi qo'ying");
								}
							},
						},
					]}
				>
					<TaskLocationListInput />
				</Form.Item>
			)}
			{targetType === "PLANT" && (
				<Form.Item
					name="plantingIds"
					label="Ekinlar"
					rules={[{ required: true, message: "Kamida bitta ekin tanlang" }]}
				>
					<PlantingMultiSelect organizationId={organizationId} />
				</Form.Item>
			)}
		</>
	);
}
